import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, message, Select, Button } from 'antd';
import './PlanUsers.css';
import { plansApi } from '../../api/plans';

const { Option } = Select;

function PlanUsers({ plan, team, orgData, onPlanUpdate, members, roles }) {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [planTeamMembers, setPlanTeamMembers] = useState([]);
	const [roleAssignments, setRoleAssignments] = useState({});
	const [roleNotes, setRoleNotes] = useState({});
	const [loading, setLoading] = useState(true);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
	const [pendingChanges, setPendingChanges] = useState({});
	const [originalState, setOriginalState] = useState({
		roleAssignments: {},
		roleNotes: {},
	});
	const [teamRoles, setTeamRoles] = useState([]);

	// Store the previous plan ID to detect when we need to refresh data
	const prevPlanIdRef = useRef(null);

	// Fetch initial data
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);

				// Get team members from plan
				setPlanTeamMembers(plan.teamMembers || []);
				setTeamRoles(team?.roles || []);

				const initialAssignments = {};
				const initialNotes = {};

				// Use teamMemberRoles from plan object
				(plan.teamMemberRoles || []).forEach((assignment) => {
					initialAssignments[assignment.userId] = {
						roleId: assignment.roleId,
						roleName: assignment.teamRole.name,
					};
					initialNotes[assignment.userId] = assignment.notes || '';
				});

				setRoleAssignments(initialAssignments);
				setRoleNotes(initialNotes);
				setOriginalState({
					roleAssignments: { ...initialAssignments },
					roleNotes: { ...initialNotes },
				});
				// Clear pending changes after successful update
				setPendingChanges({});
				setHasUnsavedChanges(false);
			} catch (error) {
				console.error('Error setting up data:', error);
				message.error('Failed to load team members and roles');
			} finally {
				setLoading(false);
			}
		};

		// Only fetch data if plan ID has changed or if team members have been updated
		if (
			plan?.id &&
			(prevPlanIdRef.current !== plan.id ||
				prevPlanIdRef.current === null)
		) {
			prevPlanIdRef.current = plan.id;
			fetchData();
		} else if (plan?.teamMembers) {
			// Just update the team members without refreshing everything else
			setPlanTeamMembers(plan.teamMembers);
		}
	}, [plan, team]); // Keep the dependencies, but use the ref to control when to fetch

	const handleOpenModal = async () => {
		try {
			// Get all users from the team members
			const teamMembers = members || [];

			// Filter out users already in the plan
			const planUserIds = new Set(planTeamMembers.map((user) => user.id));
			const available = teamMembers.filter(
				(user) => !planUserIds.has(user.id)
			);

			setAvailableUsers(available);
			setIsModalOpen(true);
		} catch (error) {
			console.error('Error in handleOpenModal:', error);
			message.error('Failed to prepare user selection');
		}
	};

	const handleAddMembers = async () => {
		try {
			const userIds = selectedUsers.map((user) => user.id);

			// Use the new method that doesn't trigger a full plan refresh
			const response = await plansApi.addTeamMembersNoRefresh(
				plan.id,
				userIds
			);

			// Update with the verified team members from the server
			setPlanTeamMembers(response.teamMembers);

			// Update the parent component with the verified team members
			const updatedPlan = {
				...plan,
				teamMembers: response.teamMembers,
			};
			onPlanUpdate(updatedPlan);

			setIsModalOpen(false);
			setSelectedUsers([]);
			setSearchTerm('');
		} catch (error) {
			console.error('Error adding members:', error);
			message.error('Failed to add members to plan');
		}
	};

	const handleRemoveMember = async (userId) => {
		try {
			// Call the API to remove the member and get verified team members
			const response = await plansApi.removeTeamMember(plan.id, userId);

			// Update with the verified team members from the server
			setPlanTeamMembers(response.teamMembers);

			// Update the parent component with the verified team members
			const updatedPlan = {
				...plan,
				teamMembers: response.teamMembers,
			};
			onPlanUpdate(updatedPlan);

			// Remove any role assignments for this user
			setRoleAssignments((prev) => {
				const newAssignments = { ...prev };
				delete newAssignments[userId];
				return newAssignments;
			});

			setRoleNotes((prev) => {
				const newNotes = { ...prev };
				delete newNotes[userId];
				return newNotes;
			});

			// Remove from pending changes
			setPendingChanges((prev) => {
				const newChanges = { ...prev };
				delete newChanges[userId];
				return newChanges;
			});

			// Check if we still have unsaved changes
			setHasUnsavedChanges(Object.keys(pendingChanges).length > 0);
		} catch (error) {
			console.error('Error removing member:', error);
			message.error('Failed to remove member from plan');
		}
	};

	// Handle role assignment change
	const handleRoleChange = (userId, roleId) => {
		const selectedRole = teamRoles.find((r) => r.id === roleId);
		setRoleAssignments((prev) => ({
			...prev,
			[userId]: {
				roleId,
				roleName: selectedRole?.name,
			},
		}));
		setPendingChanges((prev) => ({
			...prev,
			[userId]: { roleId, notes: roleNotes[userId] || '' },
		}));
		setHasUnsavedChanges(true);
	};

	// Handle role notes change
	const handleNotesChange = (userId, notes) => {
		setRoleNotes((prev) => ({
			...prev,
			[userId]: notes,
		}));
		setPendingChanges((prev) => ({
			...prev,
			[userId]: { roleId: roleAssignments[userId]?.roleId, notes },
		}));
		setHasUnsavedChanges(true);
	};

	const handleSaveChanges = async () => {
		try {
			const assignments = Object.entries(pendingChanges).map(
				([userId, data]) => ({
					userId: parseInt(userId),
					roleId: parseInt(data.roleId),
					notes: data.notes || '',
				})
			);

			// Filter out any invalid assignments
			const validAssignments = assignments.filter(
				(assignment) => !isNaN(assignment.roleId)
			);

			const updatedPlan = await plansApi.updateRoles(
				plan.id,
				validAssignments
			);

			// Update the plan in the parent component
			onPlanUpdate(updatedPlan);

			message.success('Changes saved successfully');
		} catch (error) {
			console.error('Error saving changes:', error);
			message.error('Failed to save changes');
		}
	};

	const handleCancelChanges = () => {
		setRoleAssignments(originalState.roleAssignments);
		setRoleNotes(originalState.roleNotes);
		setPendingChanges({});
		setHasUnsavedChanges(false);
	};

	const filteredUsers = availableUsers.filter((user) =>
		`${user.firstName} ${user.lastName} ${user.email}`
			.toLowerCase()
			.includes(searchTerm.toLowerCase())
	);

	// Add loading state display
	if (loading) {
		return <div>Loading team members...</div>;
	}

	return (
		<div className='plan-users-section'>
			<div className='plan-users-header'>
				<h3>Team Members ({planTeamMembers.length})</h3>
				<button
					className='add-user-button'
					onClick={handleOpenModal}>
					<span className='material-icons'>person_add</span>
				</button>
			</div>
			<div className='users-list'>
				{planTeamMembers.length > 0 ? (
					planTeamMembers.map((user) => (
						<div
							key={user.id}
							className='user-item'>
							<div className='user-info'>
								<span>
									{user.firstName} {user.lastName}
								</span>
								<Button
									type='text'
									danger
									className='remove-user-button'
									onClick={() => handleRemoveMember(user.id)}
									icon={
										<span className='material-icons'>
											close
										</span>
									}
								/>
							</div>

							<div className='user-role-container'>
								<Select
									value={roleAssignments[user.id]?.roleId}
									onChange={(value) =>
										handleRoleChange(user.id, value)
									}
									placeholder='Select role'
									className='role-select'
									allowClear>
									{(roles || []).map((role) => (
										<Option
											key={role.id}
											value={role.id}>
											{role.name}
										</Option>
									))}
								</Select>

								<textarea
									className='role-notes'
									value={roleNotes[user.id] || ''}
									onChange={(e) =>
										handleNotesChange(
											user.id,
											e.target.value
										)
									}
									placeholder='Add notes...'
									rows={2}
								/>
							</div>
						</div>
					))
				) : (
					<div className='empty-state'>
						No team members assigned yet
					</div>
				)}
			</div>

			{hasUnsavedChanges && (
				<div className='action-buttons-container'>
					<Button onClick={handleCancelChanges}>Cancel</Button>
					<Button
						type='primary'
						onClick={handleSaveChanges}
						style={{
							backgroundColor: orgData?.primaryColor,
						}}>
						Save Changes
					</Button>
				</div>
			)}

			<Modal
				title='Add Team Members'
				open={isModalOpen}
				onCancel={() => {
					setIsModalOpen(false);
					setSelectedUsers([]);
					setSearchTerm('');
				}}
				onOk={handleAddMembers}
				okButtonProps={{ disabled: selectedUsers.length === 0 }}>
				<Input
					placeholder='Search users...'
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					prefix={<span className='material-icons'>search</span>}
					className='user-search-input'
				/>
				<div className='user-selection-list'>
					{filteredUsers.length > 0 ? (
						filteredUsers.map((user) => (
							<div
								key={user.id}
								className='user-selection-item'>
								<input
									type='checkbox'
									id={`user-${user.id}`}
									checked={selectedUsers.includes(user)}
									onChange={(e) => {
										if (e.target.checked) {
											setSelectedUsers([
												...selectedUsers,
												user,
											]);
										} else {
											setSelectedUsers(
												selectedUsers.filter(
													(u) => u.id !== user.id
												)
											);
										}
									}}
								/>
								<label htmlFor={`user-${user.id}`}>
									{user.firstName} {user.lastName} (
									{user.email})
								</label>
							</div>
						))
					) : (
						<div className='no-results'>No users found</div>
					)}
				</div>
			</Modal>
		</div>
	);
}

export default PlanUsers;
